import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Brain, Target, Lightbulb, TrendingUp, CheckCircle2, 
  Clock, BookOpen, Users, ChevronDown, ChevronUp, 
  Sparkles, AlertCircle, History, Plus, X
} from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config/api';

const API = `${BACKEND_URL}/api`;

// Predefined options for quick selection
const PERSONALITY_TRAITS = [
  'competitive', 'determined', 'team-oriented', 'analytical', 
  'creative', 'resilient', 'focused', 'adaptable', 'confident', 'patient'
];

const VALUES = [
  'excellence', 'discipline', 'teamwork', 'integrity', 
  'growth', 'respect', 'perseverance', 'courage', 'balance', 'family'
];

const INTERESTS = [
  'football', 'fitness', 'sports psychology', 'nutrition', 
  'leadership', 'recovery', 'tactical analysis', 'strength training'
];

const FOCUS_AREAS = [
  'sports performance', 'mental strength', 'discipline', 
  'confidence', 'goal-setting', 'stress management', 
  'time management', 'leadership', 'recovery', 'motivation'
];

export default function LifeCoach({ childId, childName }) {
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showForm, setShowForm] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    currentSituation: '',
    personalityTraits: ['competitive', 'determined'],
    values: ['excellence', 'discipline'],
    interests: ['football', 'fitness'],
    focusAreas: ['sports performance', 'mental strength'],
    goals: [{ area: 'sports', description: '', priority: 'high', timeline: '6 months' }],
    challenges: [{ area: 'performance', description: '', impact: 'medium' }],
    timeframe: '6 months'
  });

  // Load history on mount
  useEffect(() => {
    loadHistory();
  }, [childId]);

  const loadHistory = async () => {
    try {
      setHistoryLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/ai-coach/life-coach/${childId}/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data.sessions || []);
    } catch (error) {
      console.error('Failed to load history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.currentSituation.trim()) {
      toast.error('Please describe your current situation');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/ai-coach/life-coach/${childId}`,
        {
          child_id: childId,
          ...formData,
          goals: formData.goals.filter(g => g.description.trim()),
          challenges: formData.challenges.filter(c => c.description.trim())
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setResult(response.data);
      setShowForm(false);
      toast.success('Life coaching advice generated!');
      loadHistory(); // Refresh history
    } catch (error) {
      console.error('Life coach error:', error);
      toast.error(error.response?.data?.detail || 'Failed to get coaching advice');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelection = (array, item, setter, field) => {
    if (array.includes(item)) {
      setter(prev => ({ ...prev, [field]: array.filter(i => i !== item) }));
    } else {
      setter(prev => ({ ...prev, [field]: [...array, item] }));
    }
  };

  const addGoal = () => {
    setFormData(prev => ({
      ...prev,
      goals: [...prev.goals, { area: 'sports', description: '', priority: 'medium', timeline: '6 months' }]
    }));
  };

  const removeGoal = (index) => {
    setFormData(prev => ({
      ...prev,
      goals: prev.goals.filter((_, i) => i !== index)
    }));
  };

  const updateGoal = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      goals: prev.goals.map((g, i) => i === index ? { ...g, [field]: value } : g)
    }));
  };

  const addChallenge = () => {
    setFormData(prev => ({
      ...prev,
      challenges: [...prev.challenges, { area: 'performance', description: '', impact: 'medium' }]
    }));
  };

  const removeChallenge = (index) => {
    setFormData(prev => ({
      ...prev,
      challenges: prev.challenges.filter((_, i) => i !== index)
    }));
  };

  const updateChallenge = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      challenges: prev.challenges.map((c, i) => i === index ? { ...c, [field]: value } : c)
    }));
  };

  const viewHistoricalSession = (session) => {
    setResult({
      analysis: session.response?.analysis,
      recommendations: session.response?.recommendations,
      actionPlan: session.response?.actionPlan,
      metrics: session.response?.metrics,
      resources: session.response?.resources,
      session_id: session.id,
      created_at: session.created_at
    });
    setShowHistory(false);
  };

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden" data-testid="life-coach-section">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-500/20 to-blue-500/20 border-b border-zinc-800 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl flex items-center justify-center">
              <Brain className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">AI Life Coach</h2>
              <p className="text-sm text-zinc-400">Personal development & mental performance coaching</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowHistory(!showHistory)}
              className="border-zinc-700 text-zinc-300 hover:text-white"
              data-testid="life-coach-history-btn"
            >
              <History className="h-4 w-4 mr-2" />
              History ({history.length})
            </Button>
            <Button
              onClick={() => setShowForm(!showForm)}
              className="bg-gradient-to-r from-purple-500 to-blue-500 text-white"
              data-testid="life-coach-new-session-btn"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              New Session
            </Button>
          </div>
        </div>
      </div>

      {/* History Panel */}
      {showHistory && (
        <div className="border-b border-zinc-800 p-4 bg-zinc-950/50">
          <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider mb-3">Previous Sessions</h3>
          {historyLoading ? (
            <p className="text-zinc-500 text-sm">Loading...</p>
          ) : history.length === 0 ? (
            <p className="text-zinc-500 text-sm">No previous coaching sessions</p>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {history.map((session) => (
                <button
                  key={session.id}
                  onClick={() => viewHistoricalSession(session)}
                  className="w-full p-3 bg-zinc-800/50 rounded-lg text-left hover:bg-zinc-800 transition-colors"
                >
                  <p className="text-sm text-white font-medium truncate">
                    {session.request_payload?.userProfile?.currentSituation || 'Coaching Session'}
                  </p>
                  <p className="text-xs text-zinc-500">
                    {new Date(session.created_at).toLocaleDateString()} at {new Date(session.created_at).toLocaleTimeString()}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="p-6 space-y-6 border-b border-zinc-800">
          {/* Current Situation */}
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-2">
              What's your current situation? *
            </label>
            <textarea
              value={formData.currentSituation}
              onChange={(e) => setFormData(prev => ({ ...prev, currentSituation: e.target.value }))}
              placeholder="e.g., Looking to improve my mental game and handle pressure better during matches..."
              className="w-full h-24 bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 resize-none"
              data-testid="life-coach-situation-input"
            />
          </div>

          {/* Personality Traits */}
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-2">
              Personality Traits (select all that apply)
            </label>
            <div className="flex flex-wrap gap-2">
              {PERSONALITY_TRAITS.map(trait => (
                <button
                  key={trait}
                  type="button"
                  onClick={() => toggleSelection(formData.personalityTraits, trait, setFormData, 'personalityTraits')}
                  className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                    formData.personalityTraits.includes(trait)
                      ? 'bg-purple-500/30 text-purple-300 border border-purple-500/50'
                      : 'bg-zinc-800 text-zinc-400 border border-zinc-700 hover:border-zinc-600'
                  }`}
                >
                  {trait}
                </button>
              ))}
            </div>
          </div>

          {/* Values */}
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-2">
              Core Values
            </label>
            <div className="flex flex-wrap gap-2">
              {VALUES.map(value => (
                <button
                  key={value}
                  type="button"
                  onClick={() => toggleSelection(formData.values, value, setFormData, 'values')}
                  className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                    formData.values.includes(value)
                      ? 'bg-blue-500/30 text-blue-300 border border-blue-500/50'
                      : 'bg-zinc-800 text-zinc-400 border border-zinc-700 hover:border-zinc-600'
                  }`}
                >
                  {value}
                </button>
              ))}
            </div>
          </div>

          {/* Focus Areas */}
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-2">
              Focus Areas
            </label>
            <div className="flex flex-wrap gap-2">
              {FOCUS_AREAS.map(area => (
                <button
                  key={area}
                  type="button"
                  onClick={() => toggleSelection(formData.focusAreas, area, setFormData, 'focusAreas')}
                  className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                    formData.focusAreas.includes(area)
                      ? 'bg-green-500/30 text-green-300 border border-green-500/50'
                      : 'bg-zinc-800 text-zinc-400 border border-zinc-700 hover:border-zinc-600'
                  }`}
                >
                  {area}
                </button>
              ))}
            </div>
          </div>

          {/* Goals */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-zinc-300">Goals</label>
              <Button type="button" variant="ghost" size="sm" onClick={addGoal} className="text-purple-400 hover:text-purple-300">
                <Plus className="h-4 w-4 mr-1" /> Add Goal
              </Button>
            </div>
            <div className="space-y-3">
              {formData.goals.map((goal, index) => (
                <div key={index} className="flex gap-2 items-start bg-zinc-800/50 p-3 rounded-lg">
                  <select
                    value={goal.area}
                    onChange={(e) => updateGoal(index, 'area', e.target.value)}
                    className="bg-zinc-700 border-none rounded px-2 py-1.5 text-sm text-white"
                  >
                    <option value="sports">Sports</option>
                    <option value="mental">Mental</option>
                    <option value="career">Career</option>
                    <option value="health">Health</option>
                    <option value="personal">Personal</option>
                  </select>
                  <Input
                    value={goal.description}
                    onChange={(e) => updateGoal(index, 'description', e.target.value)}
                    placeholder="Describe your goal..."
                    className="flex-1 bg-zinc-700 border-none text-white"
                  />
                  <select
                    value={goal.priority}
                    onChange={(e) => updateGoal(index, 'priority', e.target.value)}
                    className="bg-zinc-700 border-none rounded px-2 py-1.5 text-sm text-white"
                  >
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                  {formData.goals.length > 1 && (
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeGoal(index)} className="text-red-400 hover:text-red-300">
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Challenges */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-zinc-300">Challenges (optional)</label>
              <Button type="button" variant="ghost" size="sm" onClick={addChallenge} className="text-orange-400 hover:text-orange-300">
                <Plus className="h-4 w-4 mr-1" /> Add Challenge
              </Button>
            </div>
            <div className="space-y-3">
              {formData.challenges.map((challenge, index) => (
                <div key={index} className="flex gap-2 items-start bg-zinc-800/50 p-3 rounded-lg">
                  <select
                    value={challenge.area}
                    onChange={(e) => updateChallenge(index, 'area', e.target.value)}
                    className="bg-zinc-700 border-none rounded px-2 py-1.5 text-sm text-white"
                  >
                    <option value="performance">Performance</option>
                    <option value="mental">Mental</option>
                    <option value="physical">Physical</option>
                    <option value="time">Time</option>
                    <option value="other">Other</option>
                  </select>
                  <Input
                    value={challenge.description}
                    onChange={(e) => updateChallenge(index, 'description', e.target.value)}
                    placeholder="Describe your challenge..."
                    className="flex-1 bg-zinc-700 border-none text-white"
                  />
                  <select
                    value={challenge.impact}
                    onChange={(e) => updateChallenge(index, 'impact', e.target.value)}
                    className="bg-zinc-700 border-none rounded px-2 py-1.5 text-sm text-white"
                  >
                    <option value="high">High Impact</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low Impact</option>
                  </select>
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeChallenge(index)} className="text-red-400 hover:text-red-300">
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              className="bg-gradient-to-r from-purple-500 to-blue-500 text-white"
              data-testid="life-coach-submit-btn"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                  Generating Advice...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Get Coaching Advice
                </>
              )}
            </Button>
          </div>
        </form>
      )}

      {/* Results */}
      {result && (
        <div className="p-6 space-y-6">
          {result.created_at && (
            <p className="text-xs text-zinc-500">
              Session from {new Date(result.created_at).toLocaleDateString()} at {new Date(result.created_at).toLocaleTimeString()}
            </p>
          )}
          
          {/* Analysis */}
          {result.analysis && (
            <div className="bg-zinc-800/50 rounded-xl p-5">
              <h3 className="text-lg font-bold text-white flex items-center gap-2 mb-4">
                <Lightbulb className="h-5 w-5 text-yellow-400" />
                Personality Analysis
              </h3>
              <p className="text-zinc-300 mb-4">{result.analysis.personalityInsights}</p>
              
              {result.analysis.strengthsAndWeaknesses && (
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                    <h4 className="text-green-400 font-semibold mb-2">Strengths</h4>
                    <ul className="space-y-1">
                      {result.analysis.strengthsAndWeaknesses.strengths?.map((s, i) => (
                        <li key={i} className="text-sm text-zinc-300 flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                          {s}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
                    <h4 className="text-orange-400 font-semibold mb-2">Areas for Improvement</h4>
                    <ul className="space-y-1">
                      {result.analysis.strengthsAndWeaknesses.areasForImprovement?.map((a, i) => (
                        <li key={i} className="text-sm text-zinc-300 flex items-start gap-2">
                          <AlertCircle className="h-4 w-4 text-orange-400 mt-0.5 flex-shrink-0" />
                          {a}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Recommendations */}
          {result.recommendations && (
            <div className="bg-zinc-800/50 rounded-xl p-5">
              <h3 className="text-lg font-bold text-white flex items-center gap-2 mb-4">
                <Target className="h-5 w-5 text-[#02b5ff]" />
                Recommendations
              </h3>
              
              {/* Short Term */}
              {result.recommendations.shortTerm?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-[#02b5ff] font-semibold mb-2">Short-Term Actions</h4>
                  <div className="space-y-3">
                    {result.recommendations.shortTerm.map((rec, i) => (
                      <div key={i} className="bg-zinc-900/50 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-0.5 bg-[#02b5ff]/20 text-[#02b5ff] text-xs rounded-full uppercase">
                            {rec.area}
                          </span>
                          <span className="text-xs text-zinc-500">{rec.timeline}</span>
                        </div>
                        <p className="text-zinc-300 text-sm">{rec.action}</p>
                        {rec.expectedOutcome && (
                          <p className="text-xs text-zinc-500 mt-2">
                            <span className="text-green-400">Expected: </span>{rec.expectedOutcome}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Long Term */}
              {result.recommendations.longTerm?.length > 0 && (
                <div>
                  <h4 className="text-purple-400 font-semibold mb-2">Long-Term Strategy</h4>
                  {result.recommendations.longTerm.map((rec, i) => (
                    <div key={i} className="bg-zinc-900/50 rounded-lg p-4">
                      <p className="text-zinc-300 text-sm mb-3">{rec.strategy}</p>
                      
                      {rec.milestones?.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs text-zinc-500 uppercase mb-2">Milestones</p>
                          <ul className="space-y-1">
                            {rec.milestones.map((m, j) => (
                              <li key={j} className="text-sm text-zinc-400 flex items-center gap-2">
                                <div className="w-1.5 h-1.5 bg-purple-400 rounded-full" />
                                {m}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {rec.resources?.length > 0 && (
                        <div>
                          <p className="text-xs text-zinc-500 uppercase mb-2">Resources</p>
                          <div className="flex flex-wrap gap-2">
                            {rec.resources.map((r, j) => (
                              <span key={j} className="px-2 py-1 bg-zinc-800 text-zinc-400 text-xs rounded">
                                {r}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Action Plan */}
          {result.actionPlan && (
            <div className="bg-zinc-800/50 rounded-xl p-5">
              <h3 className="text-lg font-bold text-white flex items-center gap-2 mb-4">
                <TrendingUp className="h-5 w-5 text-green-400" />
                Action Plan Timeline
              </h3>
              <div className="space-y-3">
                {Object.entries(result.actionPlan).map(([period, actions]) => (
                  actions?.length > 0 && (
                    <div key={period} className="flex gap-4">
                      <div className="w-24 flex-shrink-0">
                        <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                          period === 'immediate' ? 'bg-red-500/20 text-red-400' :
                          period === '30days' ? 'bg-orange-500/20 text-orange-400' :
                          period === '90days' ? 'bg-yellow-500/20 text-yellow-400' :
                          period === '6months' ? 'bg-green-500/20 text-green-400' :
                          'bg-blue-500/20 text-blue-400'
                        }`}>
                          {period === 'immediate' ? 'Now' : period.replace('days', ' days').replace('months', ' months')}
                        </span>
                      </div>
                      <ul className="flex-1 space-y-1">
                        {actions.map((action, i) => (
                          <li key={i} className="text-sm text-zinc-300 flex items-start gap-2">
                            <Clock className="h-4 w-4 text-zinc-500 mt-0.5 flex-shrink-0" />
                            {action}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )
                ))}
              </div>
            </div>
          )}

          {/* Resources */}
          {result.resources?.recommended?.length > 0 && (
            <div className="bg-zinc-800/50 rounded-xl p-5">
              <h3 className="text-lg font-bold text-white flex items-center gap-2 mb-4">
                <BookOpen className="h-5 w-5 text-amber-400" />
                Recommended Resources
              </h3>
              <div className="grid md:grid-cols-2 gap-3">
                {result.resources.recommended.map((resource, i) => (
                  <div key={i} className="bg-zinc-900/50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded uppercase">
                        {resource.type}
                      </span>
                    </div>
                    <h4 className="text-white font-medium text-sm">{resource.title}</h4>
                    <p className="text-xs text-zinc-500 mt-1">{resource.description}</p>
                  </div>
                ))}
              </div>
              
              {result.resources.tools?.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs text-zinc-500 uppercase mb-2">Tools & Apps</p>
                  <div className="flex flex-wrap gap-2">
                    {result.resources.tools.map((tool, i) => (
                      <span key={i} className="px-3 py-1.5 bg-zinc-800 text-zinc-300 text-sm rounded-full">
                        {tool}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {result.resources.supportNetwork?.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs text-zinc-500 uppercase mb-2">Support Network</p>
                  <div className="flex flex-wrap gap-2">
                    {result.resources.supportNetwork.map((person, i) => (
                      <span key={i} className="px-3 py-1.5 bg-purple-500/20 text-purple-300 text-sm rounded-full flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        {person}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* New Session Button */}
          <div className="flex justify-center pt-4">
            <Button
              onClick={() => {
                setResult(null);
                setShowForm(true);
              }}
              variant="outline"
              className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
            >
              <Plus className="h-4 w-4 mr-2" />
              Start New Coaching Session
            </Button>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!showForm && !result && (
        <div className="p-12 text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-purple-500/20 to-blue-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Brain className="h-8 w-8 text-purple-400" />
          </div>
          <h3 className="text-lg font-bold text-white mb-2">Personal Development Coaching</h3>
          <p className="text-zinc-500 text-sm max-w-md mx-auto mb-6">
            Get AI-powered life coaching advice tailored to {childName}'s goals, challenges, and personality. 
            Improve mental strength, set better goals, and develop winning habits.
          </p>
          <Button
            onClick={() => setShowForm(true)}
            className="bg-gradient-to-r from-purple-500 to-blue-500 text-white"
          >
            <Sparkles className="h-4 w-4 mr-2" />
            Start Coaching Session
          </Button>
        </div>
      )}
    </div>
  );
}
